import { useBoolean } from 'minimal-shared/hooks';
import { useRef, useEffect, useCallback } from 'react';
import { isActiveLink, isExternalLink } from 'minimal-shared/utils';

import { usePathname } from 'src/routes/hooks';
import type { NavListProps, NavSubListProps } from '../types';

import { NavItem } from './nav-item';
import { navSectionClasses } from '../styles';
import { NavUl, NavLi, NavCollapse } from '../components';

// ----------------------------------------------------------------------

// Segment-aware prefix match: trims trailing slashes on both sides, then
// requires an exact match or a `/` boundary, so "/admin/clients" no longer
// matches "/admin/clientsX" (or a client slug like "leads-company" against
// "/admin/clients/leads"). src/utils/misc.ts's isBaseString doesn't do this
// boundary check — it's shared elsewhere and left untouched, so this helper
// stays local to the vertical nav's group matching.
function matchesPath(base: string, pathname: string): boolean {
  const trimmedBase = base.replace(/\/+$/, '');
  const trimmedPathname = pathname.replace(/\/+$/, '');
  return trimmedPathname === trimmedBase || trimmedPathname.startsWith(`${trimmedBase}/`);
}

export function NavList({
  data,
  depth,
  render,
  slotProps,
  checkPermissions,
  enabledRootRedirect,
}: NavListProps) {
  const pathname = usePathname();
  const navItemRef = useRef<HTMLButtonElement>(null);

  // Parents with children carry an array of match-paths (see NavItemDataProps),
  // and isActiveLink throws on non-string input. The array branch also falls back
  // to matchesPath so a partial subpath (e.g. `/admin/products` vs
  // `/admin/products/1`) still marks the section active.
  let isActive = false;
  if (Array.isArray(data.path)) {
    isActive = data.path.some((path) => matchesPath(path, pathname));
  } else {
    isActive = isActiveLink(pathname, data.path, data.deepMatch ?? !!data.children);
  }

  // A path can match this group's `path` yet belong to another group's item —
  // e.g. the lead inbox sits under /admin/clients but its entry lives in
  // Mission control. Applies whether `path` is an array or a single string.
  // See `excludePaths` in types.ts.
  if (isActive && data.excludePaths?.some((path) => matchesPath(path, pathname))) {
    isActive = false;
  }

  const { value: open, onFalse: onClose, onToggle } = useBoolean(isActive);

  useEffect(() => {
    if (!isActive) {
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      onToggle();
    }
  }, [data.children, onToggle]);

  const renderNavItem = () => (
    <NavItem
      ref={navItemRef}
      // slots
      path={data.path}
      icon={data.icon}
      info={data.info}
      title={data.title}
      caption={data.caption}
      // state
      open={open}
      active={isActive}
      disabled={data.disabled}
      // options
      depth={depth}
      render={render}
      hasChild={!!data.children}
      externalLink={!Array.isArray(data.path) && isExternalLink(data.path)}
      enabledRootRedirect={enabledRootRedirect}
      // styles
      slotProps={depth === 1 ? slotProps?.rootItem : slotProps?.subItem}
      // actions
      onClick={handleToggleMenu}
    />
  );

  const renderCollapse = () =>
    !!data.children && (
      <NavCollapse mountOnEnter unmountOnExit depth={depth} in={open} data-group={data.title}>
        <NavSubList
          data={data.children}
          render={render}
          depth={depth}
          slotProps={slotProps}
          checkPermissions={checkPermissions}
          enabledRootRedirect={enabledRootRedirect}
        />
      </NavCollapse>
    );

  // Hidden item by role
  if (data.allowedRoles && checkPermissions && checkPermissions(data.allowedRoles)) {
    return null;
  }

  return (
    <NavLi
      disabled={data.disabled}
      sx={{
        ...(!!data.children && {
          [`& .${navSectionClasses.li}`]: {
            '&:first-of-type': { mt: 'var(--nav-item-gap)' },
          },
        }),
      }}
    >
      {renderNavItem()}
      {renderCollapse()}
    </NavLi>
  );
}

// ----------------------------------------------------------------------

function NavSubList({
  data,
  render,
  depth = 0,
  slotProps,
  checkPermissions,
  enabledRootRedirect,
}: NavSubListProps) {
  return (
    <NavUl sx={{ gap: 'var(--nav-item-gap)' }}>
      {data.map((list) => (
        <NavList
          key={list.title}
          data={list}
          render={render}
          depth={depth + 1}
          slotProps={slotProps}
          checkPermissions={checkPermissions}
          enabledRootRedirect={enabledRootRedirect}
        />
      ))}
    </NavUl>
  );
}
